/**
 * Shared filter helpers for admin-side campaign queries.
 *
 * AI campaign generation creates campaigns with status `ai_processing` while
 * the background action is mutating the record across Stage 2/2b/3. These
 * records must be excluded from every admin-facing list/count to avoid showing
 * merchants in-flight or abandoned generations. Failed records (`failed`)
 * are similarly excluded — they're cleaned up by the cleanupAiCampaignArtifacts
 * cron after a grace period.
 *
 * We filter via POSITIVE `in` (allowlist of merchant-visible statuses) rather
 * than `notIn` because Gadget's public GraphQL API rejects `notIn` on enum
 * fields with a 400 — the `in` operator is the supported pattern for
 * frontend useFindMany calls. The backend (api.internal) supports both
 * operators, but for consistency we use the allowlist on both sides.
 *
 * Storefront safety: NOT this file's job. The storefront fetcher at
 * api/helpers/campaignHelpers.js already hardcodes status: 'active'.
 */

export const MERCHANT_VISIBLE_STATUSES = [
  "draft",
  "active",
  "paused",
  "archived",
];

// Legacy alias — kept for any downstream consumers that imported the original name.
// Functionally inverse: the four statuses NOT in EXCLUDED_AI_STATUSES.
export const EXCLUDED_AI_STATUSES = ["ai_processing", "failed"];

/**
 * Does this campaign contain survey/feedback elements?
 *
 * SINGLE SOURCE OF TRUTH. Two screens list "survey campaigns" — the standalone
 * `web/routes/survey-results.jsx` page and the Survey Results tab inside
 * `web/components/AnalyticsDashboard.jsx`. They briefly held byte-identical copies of this
 * predicate, which is the shape that drifts: add a new survey element type to one screen and the
 * other silently stops listing those campaigns, with no error anywhere.
 *
 * ⚠️ It scans `campaign.template.elements`, so any caller MUST select `template: true` — that is
 * the full element-tree JSON per record, which is why both callers pause the query until it is
 * genuinely needed rather than fetching it on first paint.
 *
 * @param {{ template?: { elements?: Array<{type?: string, data?: {action?: string}}> } }} campaign
 * @returns {boolean}
 */
export const hasSurveyElements = (campaign) => {
  const elements = campaign.template?.elements || [];
  return elements.some(el =>
    el.type === 'NsSurvey' ||
    el.type === 'NsFeedback' ||
    (el.type === 'NsButton' && el.data?.action === 'submitFeedback')
  );
};
